// Custom hook to load all products from the backend API

import { useState, useEffect, useMemo } from 'react';
import { buildProductIndex } from '../lib/productSlug';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Datos del menú "horneados" en el HTML por el prerender (script
// #__brazo_products__). Permiten que el menú y su JSON-LD aparezcan SIN depender
// del API /api/products (que el robots.txt restringe a crawlers). En el cliente
// real se refrescan luego con la llamada al API.
const SEED_ID = '__brazo_products__';
const readSeed = () => {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(SEED_ID);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
};

const useProducts = () => {
  const [seed] = useState(readSeed); // lazy: lee la semilla del HTML una sola vez
  const [brazosGitanos, setBrazosGitanos] = useState(seed?.brazosGitanos || []);
  const [galletas, setGalletas] = useState(seed?.galletas || []);
  const [galletasEspeciales, setGalletasEspeciales] = useState(seed?.galletasEspeciales || []);
  const [packs, setPacks] = useState(seed?.packs || []);
  const [pasteles, setPasteles] = useState(seed?.pasteles || []);
  const [panaderia, setPanaderia] = useState(seed?.panaderia || []);
  const [desayunos, setDesayunos] = useState(seed?.desayunos || []);
  const [menuSalado, setMenuSalado] = useState(seed?.menuSalado || []);
  const [bebidasFrias, setBebidasFrias] = useState(seed?.bebidasFrias || []);
  const [bebidasCalientes, setBebidasCalientes] = useState(seed?.bebidasCalientes || []);
  const [loadingProducts, setLoadingProducts] = useState(!seed);
  // Con semilla, loadingProducts arranca en false: el HTML prerenderizado ya
  // trae productos. Pero son los del último build. Esto marca el momento en que
  // el API real contestó, para quien no pueda decidir con datos horneados.
  const [productsRefreshed, setProductsRefreshed] = useState(false);

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      // Cache-buster fresco por cada carga (antes se evaluaba una sola vez al importar)
      const CB = `_t=${Date.now()}`;

      // Lanzar todas las peticiones EN PARALELO (antes eran 10 fetch secuenciales)
      const fetchCat = (qs) =>
        fetch(`${BACKEND_URL}/api/products?${qs}&${CB}`).catch(() => null);
      const [
        brazosResponse, galletasResponse, galletasEspecialesResponse, packsResponse,
        pastelesResponse, panaderiaResponse, desayunosResponse, menuSaladoResponse,
        bebidasFriasResponse, bebidasCalientesResponse, cakeConfigResponse
      ] = await Promise.all([
        fetchCat('category=brazos'),
        fetchCat('category=galletas'),
        fetchCat('category=galletas_especiales'),
        fetchCat('category=packs'),
        fetchCat('category=pasteles'),
        fetchCat('category=panaderia'),
        fetchCat('category=desayunos'),
        fetchCat('category=menu_sal'),
        fetchCat('category=bebidas&subcategory=frias'),
        fetchCat('category=bebidas&subcategory=calientes'),
        fetch(`${BACKEND_URL}/api/cake-config?${CB}`).catch(() => null),
      ]);

      // Brazos
      if (brazosResponse?.ok) {
        const brazosData = await brazosResponse.json();
        setBrazosGitanos(brazosData.map(b => ({
          id: b.id,
          name: b.name,
          slug: b.slug,
          description: b.description,
          priceSlice: b.priceSlice,
          priceFull: b.priceFull,
          image: b.image,
          category: b.brazo_category || 'Clásico',
          availableSlice: b.availableSlice !== false,
          availableFull: b.availableFull !== false,
          available: b.available !== false,
          bajoPedido: b.bajoPedido === true,
          sortOrder: b.sortOrder || 999
        })));
      }
      
      // Galletas
      if (galletasResponse?.ok) {
        const galletasData = await galletasResponse.json();
        const mapped = galletasData.map(g => ({
          id: g.id,
          name: g.name,
          slug: g.slug,
          description: g.description,
          priceUnit: g.priceUnit || 1.50,
          pricePack5: g.pricePack5 || 6.00,
          pricePack12: g.pricePack12 || 14.00,
          price: g.price,
          image: g.image,
          available: g.available !== false
        }));
        // Choco Chips siempre primero
        mapped.sort((a, b) => {
          const aChoco = a.name.toLowerCase().includes('choco chip');
          const bChoco = b.name.toLowerCase().includes('choco chip');
          if (aChoco && !bChoco) return -1;
          if (!aChoco && bChoco) return 1;
          return 0;
        });
        setGalletas(mapped);
      }

      // Galletas Especiales
      if (galletasEspecialesResponse?.ok) {
        const galletasEspecialesData = await galletasEspecialesResponse.json();
        setGalletasEspeciales(galletasEspecialesData.map(g => ({
          id: g.id,
          name: g.name,
          description: g.description,
          price: g.price || 0,
          image: g.image,
          available: g.available !== false
        })));
      }

      // Packs
      if (packsResponse?.ok) {
        const packsData = await packsResponse.json();
        setPacks(packsData.map(p => ({
          id: p.id,
          name: p.name,
          description: p.description,
          price: p.price || p.basePrice || 0,
          quantity: p.quantity,
          basePrice: p.basePrice,
          minQuantity: p.minQuantity,
          maxQuantity: p.maxQuantity,
          packType: p.packType,
          image: p.image,
          available: p.available !== false
        })));
      }
      
      // Pasteles
      if (pastelesResponse?.ok) {
        const pastelesData = await pastelesResponse.json();
        // Los tamaños de La Reina viven en el backend (cake_config), ya no
        // hardcodeados aquí. Los consumen tanto esta tarjeta como el configurador.
        let cakeCfg = null;
        if (cakeConfigResponse?.ok) {
          try { cakeCfg = await cakeConfigResponse.json(); } catch (_) { cakeCfg = null; }
        }
        setPasteles(pastelesData.map(p => {
          let sizes, flavors, comboPrice, comboDescription;

          if (p.isConfigurable && p.slug === 'la-reina') {
            sizes = cakeCfg?.options?.sizes || [];
          } else if (p.isTresLeches && p.slug === 'tres-leches') {
            sizes = [
              { id: 'mediana', name: 'Pequeña (Redonda)', portions: '~6 personas', price: 13, available: false },
              { id: 'grande', name: 'Grande (Rectangular)', portions: '~12 personas', price: 22 }
            ];
            flavors = [
              { id: 'clasica', name: 'Clásica', extraPrice: 0, comboGroup: 'standard' },
              { id: 'chocolate', name: 'Chocolate', extraPrice: 0, comboGroup: 'standard' },
              { id: 'maracuya', name: 'Maracuyá', extraPrice: 0, comboGroup: 'premium' },
              { id: 'ron_pope', name: 'Ron Pope (+$2)', extraPrice: 2, comboGroup: 'premium' }
            ];
            comboPrice = 28;
            comboDescription = 'Pequeña + Grande';
          }
          
          return {
            id: p.id,
            name: p.name,
            slug: p.slug,
            description: p.description,
            price: p.price || 0,
            portions: p.portions,
            image: p.image,
            isConfigurable: p.isConfigurable || false,
            isTresLeches: p.isTresLeches || false,
            bajoPedido: p.bajoPedido || false,
            sizes,
            flavors,
            comboPrice,
            comboDescription,
            available: p.available !== false
          };
        }));
      }
      
      // Panaderia
      if (panaderiaResponse?.ok) {
        const panaderiaData = await panaderiaResponse.json();
        setPanaderia(panaderiaData.map(p => ({
          id: p.id,
          name: p.name,
          slug: p.slug,
          description: p.description,
          price: p.price || 0,
          image: p.image,
          hasOption: p.hasOption || false,
          options: p.options || [],
          bajoPedido: p.bajoPedido || false,
          available: p.available !== false
        })));
      }

      // Desayunos
      if (desayunosResponse?.ok) {
        const desayunosData = await desayunosResponse.json();
        setDesayunos(desayunosData.map(d => ({
          id: d.id,
          name: d.name,
          description: d.description,
          price: d.price || 0,
          image: d.image,
          hasOption: d.hasOption || false,
          options: d.options || [],
          available: d.available !== false
        })));
      }
      
      // Menu Salado
      if (menuSaladoResponse?.ok) {
        const menuSaladoData = await menuSaladoResponse.json();
        setMenuSalado(menuSaladoData.map(m => ({
          id: m.id,
          name: m.name,
          description: m.description,
          price: m.price || 0,
          image: m.image,
          hasOption: m.hasOption || false,
          options: m.options || [],
          available: m.available !== false
        })));
      }
      
      // Bebidas Frias
      if (bebidasFriasResponse?.ok) {
        const bebidasFriasData = await bebidasFriasResponse.json();
        setBebidasFrias(bebidasFriasData.map(b => ({
          id: b.id,
          name: b.name,
          description: b.description,
          price: b.price || 0,
          image: b.image,
          hasOption: b.hasOption || false,
          options: b.options || [],
          available: b.available !== false
        })));
      }
      
      // Bebidas Calientes
      if (bebidasCalientesResponse?.ok) {
        const bebidasCalientesData = await bebidasCalientesResponse.json();
        setBebidasCalientes(bebidasCalientesData.map(b => ({
          id: b.id,
          name: b.name,
          description: b.description,
          price: b.price || 0,
          image: b.image,
          hasOption: b.hasOption || false,
          options: b.options || [],
          available: b.available !== false
        })));
      }
      
    } catch (error) {
      console.error('Error loading products from backend:', error);
    } finally {
      setLoadingProducts(false);
      setProductsRefreshed(true);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // Expone los datos ya cargados/mapeados para que el prerender los serialice en
  // el HTML (window.__BRAZO_PRODUCTS__ → script #__brazo_products__).
  useEffect(() => {
    if (typeof window === 'undefined' || loadingProducts) return;
    window.__BRAZO_PRODUCTS__ = {
      brazosGitanos, galletas, galletasEspeciales, packs, pasteles,
      panaderia, desayunos, menuSalado, bebidasFrias, bebidasCalientes,
    };
  }, [loadingProducts, brazosGitanos, galletas, galletasEspeciales, packs,
    pasteles, panaderia, desayunos, menuSalado, bebidasFrias, bebidasCalientes]);

  // Índice slug → producto, la identidad que usa /producto/<slug> y el botón de
  // compartir. Se deriva aquí porque este hook ya es el único sitio donde todos
  // los tipos quedan normalizados con la misma forma.
  const productIndex = useMemo(
    () => buildProductIndex({
      brazosGitanos, galletas, galletasEspeciales, pasteles,
      panaderia, desayunos, menuSalado, bebidasFrias, bebidasCalientes,
    }),
    [brazosGitanos, galletas, galletasEspeciales, pasteles,
      panaderia, desayunos, menuSalado, bebidasFrias, bebidasCalientes]
  );

  return {
    brazosGitanos,
    galletas,
    galletasEspeciales,
    packs,
    pasteles,
    panaderia,
    desayunos,
    menuSalado,
    bebidasFrias,
    bebidasCalientes,
    productIndex,
    productsRefreshed,
    loadingProducts
  };
};

export default useProducts;
